// frontend/src/components/dashboard/Dashboard.js
import React, { useState, useCallback } from 'react';
import {
    Box, Typography, Button, IconButton, Tooltip, Chip, Alert, Divider,
} from '@mui/material';
import { Refresh, Add, ConfirmationNumber, TableChart } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { getSummary, getMyDashboard } from '../../services/endpoints';
import { isManagerRole } from '../../constants/domain';
import solidproLogo from '../../assets/solidpro-logo.svg';
import TopCards from './TopCards';
import MyDashboard from './MyDashboard';
import MetricsPanel from './MetricsPanel';
import socket from '../../services/socket';

const REFRESH_MS = Number(process.env.REACT_APP_REFRESH_MS) || 15 * 60 * 1000;

const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [mine, setMine] = useState(null);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async () => {
        try {
            const [summaryRes, myRes] = await Promise.all([
                getSummary(),
                getMyDashboard(),
            ]);
            setSummary(summaryRes.data);
            setMine(myRes.data);
            setError(null);
        } catch (err) {
            const message = err.response?.data?.message || err.message;
            setError(message);
        }
    }, []);

    const { lastRefresh, refreshing, refresh } = useAutoRefresh(fetchAll, REFRESH_MS);

    // Live updates: refresh when anyone changes a status.
    React.useEffect(() => {
        const onChange = () => fetchAll();
        socket.on('statusUpdate', onChange);
        socket.on('partCreated', onChange);
        socket.on('partsImported', onChange);
        return () => {
            socket.off('statusUpdate', onChange);
            socket.off('partCreated', onChange);
            socket.off('partsImported', onChange);
        };
    }, [fetchAll]);

    const manager = isManagerRole(user?.role);

    return (
        <Box>
            {/* Header */}
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 2,
                alignItems: 'center', justifyContent: 'space-between', mb: 2,
            }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Welcome, {user?.full_name || 'there'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="img" src={solidproLogo} alt="Solidpro" sx={{ height: 16 }} />
                        {lastRefresh && (
                            <Typography variant="body2" color="text.secondary">
                                {`updated ${lastRefresh.toLocaleTimeString('en-GB')}`}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip size="small" variant="outlined"
                        label={`Auto-refresh every ${Math.round(REFRESH_MS / 60000)} min`} />
                    <Tooltip title="Refresh now">
                        <IconButton onClick={refresh} disabled={refreshing}>
                            <Refresh sx={{
                                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                                '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
                            }} />
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<TableChart />}
                        onClick={() => navigate('/parts')}>
                        Parts Tracker
                    </Button>
                    {manager && (
                        <>
                            <Button variant="outlined" startIcon={<ConfirmationNumber />}
                                onClick={() => navigate('/tickets')}>
                                Tickets
                            </Button>
                            <Button variant="contained" startIcon={<Add />}
                                onClick={() => navigate('/parts?add=1')}>
                                Add Part
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    Could not load the dashboard: {error}
                </Alert>
            )}

            {/* My Dashboard */}
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                My Dashboard
            </Typography>
            <MyDashboard data={mine} loading={refreshing && !mine} />

            <Divider sx={{ my: 3 }} />

            {/* Top Cards matrix */}
            <TopCards data={summary} loading={refreshing && !summary} />

            <Divider sx={{ my: 3 }} />

            <MetricsPanel />
        </Box>
    );
};

export default Dashboard;
